import type { ChatMessageView, ErrorMsg } from '@shared/protocol/messages';
import { CHAT_LIMITS } from '@shared/protocol/messages';
import type { App } from '../app/App';
import { button } from './components';
import { h, setChildren } from './dom';
import { t } from '../i18n';

/**
 * The world chat panel (13+ only; callers check canUseWorldChat). Built once
 * per scene and re-attached on every render, so typing is never lost.
 * Message text is untrusted: it only ever goes into text nodes.
 */
export function worldChatPanel(app: App): { el: HTMLElement; dispose: () => void } {
  const log = h('ol', { class: 'chat-log', 'aria-live': 'polite', 'aria-label': t('Messages') });
  const input = h('input', { class: 'hand-input chat-input', maxlength: String(CHAT_LIMITS.maxLength), placeholder: t('Say something nice…'), 'aria-label': t('Message') });
  const reported = new Set<string>();

  const line = (m: ChatMessageView): HTMLElement => {
    const mine = m.playerId === app.room.playerId;
    const li = h('li', { class: `chat-line${mine ? ' mine' : ''}` }, h('span', { class: 'chat-name' }, m.name), h('span', { class: 'chat-text' }, m.text));
    if (!mine) {
      const flag = h('button', { type: 'button', class: 'chat-report', title: t('Report this message'), 'aria-label': t('Report this message'), disabled: reported.has(m.id) }, '⚑');
      flag.addEventListener('click', () => {
        flag.disabled = true;
        app.chat.report(m.id).then(
          () => {
            reported.add(m.id);
            app.toast(t('Thanks — a moderator will take a look.'), 'success');
          },
          (e: ErrorMsg) => {
            flag.disabled = false;
            app.toast(e.message, 'error');
          },
        );
      });
      li.append(flag);
    }
    return li;
  };

  const draw = (messages: readonly ChatMessageView[]): void => {
    const atBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 40;
    setChildren(log, messages.length ? messages.map(line) : h('li', { class: 'chat-empty' }, t('No messages yet. Say hi!')));
    if (atBottom) log.scrollTop = log.scrollHeight;
  };

  let sending = false;
  const send = (): void => {
    const text = input.value.trim();
    if (!text || sending) return;
    sending = true;
    app.chat.send(text).then(
      () => {
        input.value = '';
        log.scrollTop = log.scrollHeight;
      },
      (e: ErrorMsg) => app.toast(e.message, 'error'),
    ).finally(() => {
      sending = false;
      input.focus();
    });
  };
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.isComposing) {
      ev.preventDefault();
      send();
    }
  });

  const el = h(
    'section',
    { class: 'world-chat sticky-note blue', 'aria-label': t('World chat') },
    h('h2', null, t('World chat')),
    h('p', { class: 'hand-hint' }, t('Be kind! Rude words are hidden, and ⚑ reports a message to a moderator.')),
    log,
    h('div', { class: 'chat-form' }, input, button(t('Send'), { variant: 'primary', color: 'yellow', small: true, sfx: app, onClick: send })),
  );

  const offChange = app.chat.on('change', draw);
  const offError = app.chat.on('error', (e) => setChildren(log, h('li', { class: 'chat-empty' }, e.message)));
  draw(app.chat.messages);
  app.chat.open();
  return {
    el,
    dispose: () => {
      offChange();
      offError();
      app.chat.close();
    },
  };
}
